export interface Derivatives {
  /** Natural dimensions of the ORIGINAL, which is what the attachment records. */
  width: number;
  height: number;
  /** Videos only. */
  duration_ms: number | null;
  /** A list row. */
  thumbnail: Blob | null;
  /** A phone screen at 3x, so the detail hero is not the archival master. */
  display: Blob | null;
}

const EDGES = { thumbnail: 512, display: 1600 } as const;
const QUALITY = 0.8;
/** A frame this far in, because frame zero is usually black or a fade-in. */
const POSTER_AT = 0.1;
const POSTER_AT_MOST = 1;
/** No single step of reading a video may hold an upload open longer than this. */
const VIDEO_TIMEOUT_MS = 5000;

/**
 * Renders the WebP copies the app actually displays, in the browser.
 *
 * The alternative is the server downloading a 25 MB original out of object
 * storage just to resize it, on every upload, for a list row — and for a video,
 * a 200 MB one plus an ffmpeg it does not have.
 *
 * A video is a picture source like any other as far as `drawImage` is
 * concerned, so both kinds end in the same encoder and both sizes come out of
 * one decode: turning a full-resolution frame into a bitmap is the expensive
 * part and there is no reason to pay for it twice.
 */
export async function makeDerivatives(file: File): Promise<Derivatives | null> {
  if (file.type.startsWith("video/")) return fromVideo(file);
  if (file.type.startsWith("image/")) return fromImage(file);
  return null;
}

async function fromImage(file: File): Promise<Derivatives | null> {
  let bitmap: ImageBitmap;
  try {
    // Without this the default is "none" on some WebViews, and an EXIF-rotated
    // phone photo yields a thumbnail turned 90 degrees against the original the
    // browser auto-orients — and swapped dimensions driving the hero's aspect box.
    bitmap = await createImageBitmap(file, { imageOrientation: "from-image" });
  } catch {
    // HEIC and other formats the decoder does not know: ship without derivatives.
    return null;
  }

  try {
    const display = await encode(bitmap, bitmap.width, bitmap.height, EDGES.display);
    return {
      width: bitmap.width,
      height: bitmap.height,
      duration_ms: null,
      thumbnail: await encode(bitmap, bitmap.width, bitmap.height, EDGES.thumbnail),
      // A re-encode that saved nothing is a second copy to store and a second
      // request to make. The original is already the better answer.
      display: display && display.size < file.size ? display : null,
    };
  } finally {
    bitmap.close();
  }
}

/**
 * Dimensions and a poster frame, from one `<video>` that is never in the page.
 *
 * The dimensions come from metadata and the frame from a seek, and those two
 * can succeed independently: a clip whose codec this WebView will not decode
 * still records how long it is and how big, and simply ships without a poster.
 * That is the same bargain HEIC gets above.
 */
async function fromVideo(file: File): Promise<Derivatives | null> {
  const url = URL.createObjectURL(file);
  const video = document.createElement("video");
  // Muted and inline or iOS refuses to decode anything without a gesture, and
  // `metadata` alone does not guarantee there is a frame to draw.
  video.muted = true;
  video.playsInline = true;
  video.preload = "auto";
  video.src = url;

  try {
    await settles(video, "loadedmetadata", () => video.readyState >= HAVE_METADATA);
    const size = { width: video.videoWidth, height: video.videoHeight };
    const duration_ms = Math.round(video.duration * 1000) || 0;

    let poster: { thumbnail: Blob | null; display: Blob | null } = {
      thumbnail: null,
      display: null,
    };
    try {
      await settles(video, "loadeddata", () => video.readyState >= HAVE_CURRENT_DATA);

      // Seeking to where we already are fires no `seeked` at all, which would
      // sit here until the timeout and cost the poster. A clip too short to
      // have a tenth of a second in it keeps the frame it is already showing.
      const at = Math.min(POSTER_AT_MOST, (video.duration || 0) * POSTER_AT);
      if (at > 0) {
        video.currentTime = at;
        await settles(video, "seeked", () => !video.seeking && video.currentTime >= at);
      }

      poster = {
        thumbnail: await encode(video, size.width, size.height, EDGES.thumbnail),
        // No size comparison here: a still is always smaller than the clip.
        display: await encode(video, size.width, size.height, EDGES.display),
      };
    } catch {
      /* no poster; the record still gets its dimensions */
    }

    return { ...size, duration_ms, ...poster };
  } catch {
    return null;
  } finally {
    video.removeAttribute("src");
    video.load();
    URL.revokeObjectURL(url);
  }
}

const HAVE_METADATA = 1;
const HAVE_CURRENT_DATA = 2;

/**
 * Resolves on the named event, and refuses to wait forever for it.
 *
 * `already` is what keeps this honest: these events can have fired before we
 * got a chance to listen — `loadeddata` in particular often lands in the same
 * task as `loadedmetadata` for a small clip — and a listener attached after the
 * fact waits out the whole timeout for something that already happened.
 */
function settles(
  video: HTMLVideoElement,
  event: "loadedmetadata" | "loadeddata" | "seeked",
  already: () => boolean,
) {
  if (already()) return Promise.resolve();
  return new Promise<void>((resolve, reject) => {
    const done = (outcome: () => void) => {
      clearTimeout(timer);
      video.removeEventListener(event, ok);
      video.removeEventListener("error", fail);
      outcome();
    };
    const ok = () => done(resolve);
    const fail = () => done(() => reject(new Error(event)));
    const timer = setTimeout(fail, VIDEO_TIMEOUT_MS);

    video.addEventListener(event, ok, { once: true });
    video.addEventListener("error", fail, { once: true });
  });
}

async function encode(
  source: CanvasImageSource,
  width: number,
  height: number,
  maxEdge: number,
): Promise<Blob | null> {
  if (!width || !height) return null;
  const scale = Math.min(1, maxEdge / Math.max(width, height));
  const canvas = document.createElement("canvas");
  canvas.width = Math.max(1, Math.round(width * scale));
  canvas.height = Math.max(1, Math.round(height * scale));

  const context = canvas.getContext("2d");
  if (!context) return null;
  context.drawImage(source, 0, 0, canvas.width, canvas.height);

  return new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, "image/webp", QUALITY));
}
